import { extrasOf } from './extras'
import type { ExtraOffer, OfferChoice, OfferRow } from './offer-extras'
// "Book them in" (Liam, 19 Oct 2026: "I've now arranged with Emma a date and a time through the messages, which I think
// is a pretty common flow ... it's not clear where to go next"; drawn https://claude.ai/artifact/HQXDMSa4qACfQ9XS58ci8m,
// he picked A, the price typed on the booking sheet). From an enquiry's card on Messages or its page on New jobs: first,
// is this person already a customer (only when one looks like them, as Make them a customer asks); then the Book a
// visit sheet, with the day and time arranged turning their switches on. Nothing is saved until Book, and then
// everything at once (POST /api/leads/:id/book, functions/lib/book-lead.ts). "Yes, this is them" links the enquiry to
// that customer (one save) and opens their own sheet.
import { addressLine } from './address-words'
import { api, getQueue } from './api'
import { openBooking, type BookDraft } from './booking'
import { findCustomerMatches, type MatchReason } from './lead-conversion'
import { firstName } from './late-message'
import { postcodeWords } from './round-config'
import type { Customer, Lead } from './types'
import { clear, el, toast } from './ui'

export const bookPath = (l: Pick<Lead, 'id'>): string => `/api/leads/${l.id}/book`

// A booking for this enquiry still on its way (no signal): the card says so and a second one is never started.
export function bookingWaiting(l: Pick<Lead, 'id'>): boolean {
  return getQueue().some((q) => q.path === bookPath(l) && (q.status === 'queued' || q.status === 'sending'))
}

const pricedExtras = (json: string): ExtraOffer['items'] => extrasOf(json).filter(e => e.each_pence !== null) as ExtraOffer['items']

interface QuoteOverlap { included: number[]; uncertain: number[]; insidesIncluded: boolean; insidesUncertain: boolean }

const REASON_WORDS: Record<MatchReason, string> = { name: 'same name', phone: 'same phone number', address: 'same address' }

// A second tap while the first is still reading the customers opens nothing more (Sonnet, 19 Oct 2026).
let opening = false

export async function bookEnquiry(lead: Lead): Promise<void> {
  const who = firstName(lead.name) || lead.name
  if (bookingWaiting(lead)) { toast(`Booking ${who} in is still waiting to save. It goes as soon as it can.`); return }
  if (opening || document.querySelector('dialog.book-choose-dialog[open], dialog.booking-dialog[open]')) return
  opening = true
  // Read the selected generation again with signal. Book stays available offline with its last saved choice.
  if (lead.regular_offer_choice_id) try { lead = (await api<{ lead: Lead }>(`/api/leads/${lead.id}`)).lead } catch { /* cached enquiry */ }
  let acceptedOffer: { choice: OfferChoice; offer: ExtraOffer } | undefined
  let offersDeferred = false
  if (lead.has_extra_offer) try {
    const { offer, quote_overlap } = await api<{ offer: OfferRow | null; quote_overlap?: QuoteOverlap | null }>(`/api/leads/${lead.id}/extra-offers`)
    if (offer && !offer.booked_visit_id) {
      const picked = await chooseAccepted(lead, offer, quote_overlap ?? undefined)
      if (!picked) { opening = false; return }
      acceptedOffer = picked
    }
  } catch { offersDeferred = true }
  if (lead.converted_customer_id) {
    opening = false
    if (acceptedOffer) openBooking({ customerId: lead.converted_customer_id, linkLead: lead, acceptedOffer, addOfferToVisit: true })
    else toast('Open this enquiry with signal to choose the offered extras.')
    return
  }
  let customers: Customer[] | null = null
  try { customers = (await api<{ customers: Customer[] }>('/api/customers')).customers } catch { customers = null } finally { opening = false }
  const choose = (draft?: BookDraft): void => chooseCustomer(lead, customers, draft, acceptedOffer, offersDeferred)
  // Someone on the round looks like them: ask first. Nobody does, or the list could not be read: the sheet, with
  // "Already a customer? Pick them" one tap away.
  if (customers && findCustomerMatches(lead, customers).length) { choose(); return }
  openBooking({ enquiry: lead, pickCustomer: choose, acceptedOffer, offersDeferred })
}

// Is this enquiry one of your customers already? The same words and rule as Make them a customer (new-jobs.ts
// openConvert): the same person only, never a neighbour; nothing is linked without "Yes, this is them".
function chooseCustomer(lead: Lead, customers: Customer[] | null, draft?: BookDraft, acceptedOffer?: { choice: OfferChoice; offer: ExtraOffer }, offersDeferred = false): void {
  const who = firstName(lead.name) || lead.name
  // Drawn as the Book a visit sheet is (rising from the foot on a phone, at the side on a computer): it opens from
  // Messages, where New jobs' own dialog styles are not on the page.
  const dlg = el('dialog', { class: 'booking-dialog book-choose-dialog', 'aria-labelledby': 'book-choose-title' }) as HTMLDialogElement
  const list = el('div', { class: 'customer-picker' })
  const search = el('input', { type: 'search', placeholder: postcodeWords('Name, address or postcode'), 'aria-label': 'Search your customers' }) as HTMLInputElement
  const close = (): void => { if (dlg.open) dlg.close() }
  dlg.addEventListener('close', () => dlg.remove())

  // "Yes, this is them" saves nothing yet: their own sheet opens, and booking the visit there puts the enquiry on their
  // page (booking.ts linkLead), so cancelling it leaves everything as it was (Sol, 19 Oct 2026).
  const linkTo = (c: Customer): void => {
    close()
    openBooking({ customerId: c.id, arranged: true, linkLead: lead, acceptedOffer, offersDeferred, ...(draft ? { draft: { date: draft.date, time: draft.time, worker: draft.worker, duration: draft.duration, note: draft.note, extras: draft.extras } } : {}) })
  }
  const row = (c: Customer, reasons?: MatchReason[]): HTMLElement => {
    const yes = el('button', { type: 'button', class: 'btn-secondary', 'aria-label': `Yes, ${lead.name} is ${c.name}` }, 'Yes, this is them') as HTMLButtonElement
    yes.addEventListener('click', () => linkTo(c))
    return el('div', { class: 'customer-choice' },
      el('strong', {}, c.name),
      el('span', {}, addressLine(c.address, c.postcode, ' · ')),
      ...(reasons?.length ? [el('span', { class: 'muted' }, reasons.map((r) => REASON_WORDS[r]).join(', '))] : []),
      yes)
  }
  const draw = (): void => {
    clear(list)
    if (!customers) { list.append(el('p', { class: 'muted' }, "Couldn't read your customers just now. If they are new to you, book them in as a new customer.")); return }
    const q = search.value.trim().toLowerCase().replace(/\s/g, '')
    if (!q) {
      const matches = findCustomerMatches(lead, customers)
      if (!matches.length) list.append(el('p', { class: 'muted' }, 'Nobody on your round has the same name, number or address. Search if you think they are.'))
      for (const m of matches) list.append(row(m.customer, m.reasons))
      return
    }
    const hits = customers.filter((c) => [c.name, c.address, c.postcode].some((v) => (v ?? '').toLowerCase().replace(/\s/g, '').includes(q)))
    for (const c of hits.slice(0, 30)) list.append(row(c))
    if (!hits.length) list.append(el('p', { class: 'muted' }, 'No customers match this search.'))
  }
  search.addEventListener('input', draw)
  const fresh = el('button', { type: 'button', class: 'btn-primary' }, `No, book ${who} in as a new customer`) as HTMLButtonElement
  fresh.addEventListener('click', () => { close(); openBooking({ enquiry: lead, acceptedOffer, offersDeferred, ...(draft ? { draft } : {}), pickCustomer: (d) => chooseCustomer(lead, customers, d, acceptedOffer, offersDeferred) }) })
  const cancel = el('button', { type: 'button' }, 'Cancel') as HTMLButtonElement
  cancel.addEventListener('click', close)
  const shut = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close') as HTMLButtonElement
  shut.addEventListener('click', close)
  dlg.append(
    el('header', { class: 'booking-head' }, el('h2', { id: 'book-choose-title' }, `Is ${who} already one of your customers?`), shut),
    el('div', { class: 'booking-content' },
      el('p', { class: 'field-help' }, 'Pick them only if they are the same person. A neighbour or a friend is a new customer.'),
      search, list,
      el('div', { class: 'booking-footer' }, cancel, fresh)),
  )
  draw()
  document.body.append(dlg)
  dlg.showModal()
}

// The owner chooses even for a plain "yes". Closing this sheet writes nothing.
function chooseAccepted(lead: Lead, row: OfferRow, overlap?: QuoteOverlap): Promise<{ choice: OfferChoice; offer: ExtraOffer } | null> {
  return new Promise(resolve => {
    const offered = pricedExtras(row.offered_json)
    const replies = (() => { try { return JSON.parse(lead.quote_replies ?? '[]') as {body: string}[] } catch { return [] } })()
    const said = replies.at(-1)?.body ?? ''
    const named = (name: string): boolean => !/\b(no(?!\s+rush\b)|not|just|only)\b/i.test(said) && new RegExp(`\\b${name}\\b`, 'i').test(said)
    const dlg = el('dialog', { class: 'booking-dialog offer-accept-dialog', 'aria-labelledby': 'offer-accept-title' }) as HTMLDialogElement
    const choices = el('div', { class: 'offer-accept-choices' })
    const selected: HTMLInputElement[] = []
    const check = (name: string, on = false): HTMLInputElement => {
      const box = el('input', { type: 'checkbox' }) as HTMLInputElement
      box.checked = on; choices.append(el('label', {}, box, name)); return box
    }
    offered.forEach((e, i) => selected.push(check(`${e.name}${overlap?.included.includes(i) ? ' · Already in quote, no extra charge' : ''}`,  e.kind !== 'other' && named(e.kind === 'conservatory' ? 'conservatory' : 'gutters?'))))
    const insides = row.insides_pence === null ? null : check(`Insides${overlap?.insidesIncluded ? ' · Already in quote, no extra charge' : ''}`, named('insides?'))
    const all = el('button', { type: 'button', class: 'link-button' }, 'All extras')
    const none = el('button', { type: 'button', class: 'link-button' }, 'No extras')
    all.addEventListener('click', () => { for (const c of [...selected, ...(insides ? [insides] : [])]) c.checked = true })
    none.addEventListener('click', () => { for (const c of [...selected, ...(insides ? [insides] : [])]) c.checked = false })
    const next = el('button', { type: 'button', class: 'btn-primary' }, 'Continue to Book')
    const error = el('p', { class: 'form-error', role: 'alert' })
    let answer: { choice: OfferChoice; offer: ExtraOffer } | null = null
    next.addEventListener('click', () => {
      const accepted = selected.flatMap((c, i) => c.checked ? [i] : [])
      if (accepted.some(i => overlap?.uncertain.includes(i)) || (insides?.checked && overlap?.insidesUncertain)) { error.textContent = 'Check this work against the quote. Its custom work is not clear enough to add another charge. Update the quote first.'; return }
      answer = { choice: { id: row.id, version: row.version, accepted, insides: !!insides?.checked },
        offer: { items: accepted.filter(i => !overlap?.included.includes(i)).map(i => offered[i]!), insides: insides?.checked && !overlap?.insidesIncluded ? { pence: row.insides_pence!, every: !!row.insides_every } : null } }
      dlg.close()
    })
    const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
    close.addEventListener('click', () => dlg.close())
    dlg.addEventListener('close', () => { dlg.remove(); resolve(answer) })
    dlg.append(el('header', { class: 'booking-head' }, el('h2', { id: 'offer-accept-title' }, 'Which extras did they accept?'), close),
      el('div', { class: 'booking-content' }, el('p', {}, said || 'Check their reply before booking.'),
        el('p', { class: 'muted' }, 'A plain yes could mean windows only. Choose none, some or all. Accepted extras go on the first visit.'),
        choices, error, el('div', {}, none, all), el('div', { class: 'booking-footer' }, next)))
    document.body.append(dlg); dlg.showModal()
  })
}
